import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Download } from "lucide-react";

interface FAQProps {
    program8Days?: any;
    nasaProgram?: any;
    hideBrochureButtons?: boolean;
}

const FAQ: React.FC<FAQProps> = ({ program8Days, nasaProgram, hideBrochureButtons }) => {
    const [openQuestion, setOpenQuestion] = useState<number>(0); // First question open by default

    const faqData = [
        {
            question: "What NASA Training Program?",
            answer: "The NASA Training Program is a unique and limited STEM field workshop conducted directly by NASA at the Kennedy Space Center in Florida. Through authentic mission simulations and hands-on engineering projects, participants experience real NASA methodologies and cutting-edge technology as part of a comprehensive space science education.",
        },
        {
            question: "Am I going to receive a certificate at the end of the program?",
            answer: "Yes, participants who successfully complete the program receive an official NASA certificate, recognizing their training and achievements in the program.",
        },
        {
            question: "How long is the program?",
            answer: "The program is offered in two different formats: an 8-day program and a more in-depth 10-day program. Both versions provide immersive, hands-on experiences at the Kennedy Space Center.",
        },
        {
            question: "Who can attend the program?",
            answer: "The program is designed for students with a strong interest in STEM fields. It is ideal for high school and university students who are passionate about space science, engineering, and technology, and who wish to learn through NASA's authentic mission-based approach.",
        },
        {
            question: "What will participants experience in the program?",
            answer: "Participants will engage in realistic NASA mission simulations and hands-on engineering projects, using the same tools and methodologies employed by NASA professionals. The training includes comprehensive space science education and direct exposure to cutting-edge technology at the Kennedy Space Center.",
        },
    ];

    const toggleQuestion = (index: number) => {
        setOpenQuestion(openQuestion === index ? -1 : index);
    };

    return (
        <div className="py-16 md:py-20 lg:py-24 bg-[#FFFFFF]">
            <div className="max-w-7xl mx-auto px-4">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-12"
                >
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#01003A] mb-6 leading-tight">Frequently Asked Question</h2>
                    <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
                        Explore quick answers about our programs, benefits, pricing, and activities - everything
                        <br />
                        you need to start your cosmic journey with confidence.
                    </p>
                </motion.div>

                {/* FAQ Items */}
                <div className="bg-[#FFFFFF] rounded-2xl overflow-hidden shadow-sm">
                    {faqData.map((faq, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className={`border-b border-[#0000001A] ${index === faqData.length - 1 ? "border-b-0" : ""} ${openQuestion === index ? "bg-[#00000005]" : ""}`}
                        >
                            {/* Question */}
                            <button
                                onClick={() => toggleQuestion(index)}
                                className="w-full px-6 md:px-8 py-6 md:py-8 text-left flex items-center justify-between hover:bg-[#00000005] transition-colors duration-200"
                            >
                                <h3 className="text-lg md:text-xl font-medium text-[#111111] pr-4">{faq.question}</h3>
                                <div className="flex-shrink-0 w-8 h-8 border border-[#0000001A] rounded-full flex items-center justify-center">
                                    {openQuestion === index ? <Minus className="h-4 w-4 text-[#000000]" /> : <Plus className="h-4 w-4 text-[#000000]" />}
                                </div>
                            </button>

                            {/* Answer */}
                            <AnimatePresence>
                                {openQuestion === index && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-6 md:px-8 pb-6 md:pb-8">
                                            <p className="text-[#000000B2] leading-relaxed">{faq.answer}</p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>

                {/* Download Brochure Buttons */}
                {!hideBrochureButtons && (
                  <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                      viewport={{ once: true }}
                      className="text-center mt-12"
                  >
                      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-2xl mx-auto">
                          {/* 13-Day Program Brochure */}
                          <button
                              onClick={() => {
                                  const link = document.createElement("a");                                  
                                  link.href = "/documents/nasa_13days.pdf";
                                  link.download = "nasa_13days.pdf";
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                              }}
                              className="relative flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 hover:scale-105 hover:shadow-xl transition-all duration-300 shadow-lg flex items-center justify-center group min-w-[200px] overflow-hidden"
                          >
                              <span className="relative z-10 mr-2 group-hover:font-semibold transition-all duration-300">{program8Days?.duration || "13 Days"} Brochure</span>
                              <Download className="relative z-10 h-5 w-5 group-hover:translate-y-0.5 transition-transform duration-300" />
                          </button>

                          {/* 8-Day Program Brochure */}
                          <button
                              onClick={() => {
                                  const link = document.createElement("a");
                                  link.href = "/documents/nasa_8days.pdf";
                                  link.download = "nasa_8days.pdf";
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                              }}
                              className="relative flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 hover:scale-105 hover:shadow-xl transition-all duration-300 shadow-lg flex items-center justify-center group min-w-[200px] overflow-hidden"
                          >
                              <span className="relative z-10 mr-2 group-hover:font-semibold transition-all duration-300">{nasaProgram?.duration || "8 Days"} Brochure</span>
                              <Download className="relative z-10 h-5 w-5 group-hover:translate-y-0.5 transition-transform duration-300" />
                          </button>
                      </div>
                  </motion.div>
                )}
            </div> 
        </div>
    );
};

export default FAQ;
